// SPDX-License-Identifier: AGPL-3.0-or-later
// Small shared SVG helpers. Bespoke charts live in their views; this holds the
// bits reused across them (sparklines, tip attributes, tick helpers).
import { esc } from './format';

/** data-tip attribute with escaped content (tooltip renders it as textContent). */
export function tipAttr(text: string): string {
  return `data-tip="${esc(text)}"`;
}

/** "Nice" upper bound at or above max, for axis ticks. */
export function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  const n = max / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

/** A polyline `points` string for a series over an index axis. */
export function linePoints(vals: (number | null)[], x: (i: number) => number, y: (v: number) => number): string {
  return vals
    .map((v, i) => (v == null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`))
    .filter(Boolean)
    .join(' ');
}

/** Compact inline sparkline (SVG markup string). */
export function sparkline(vals: (number | null)[], colour: string, w = 108, h = 26): string {
  const nums = vals.filter((v): v is number => v != null && Number.isFinite(v));
  if (nums.length < 2) return `<svg class="spark" width="${w}" height="${h}" aria-hidden="true"></svg>`;
  const min = Math.min(...nums), max = Math.max(...nums);
  const span = max - min || 1;
  const x = (i: number) => (i / (vals.length - 1)) * (w - 2) + 1;
  const y = (v: number) => h - 2 - ((v - min) / span) * (h - 4);
  const pts = linePoints(vals, x, y);
  const lastI = vals.length - 1;
  const last = vals[lastI];
  return `<svg class="spark" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">
    <polyline points="${pts}" fill="none" stroke="${colour}" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>
    ${last != null ? `<circle cx="${x(lastI).toFixed(1)}" cy="${y(last).toFixed(1)}" r="2" fill="${colour}"/>` : ''}
  </svg>`;
}
